/**
 * usePageTracking Hook
 * Automatically tracks page views and maintains heartbeat for activity monitoring
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, startHeartbeat, stopHeartbeat, updateHeartbeatPath } from '../services/trackingService';

/**
 * Hook to track page views and user activity
 * @param {boolean} enabled - Whether tracking is enabled (default: true)
 */
const usePageTracking = (enabled = true) => {
  const location = useLocation();
  const previousPath = useRef(null);
  
  useEffect(() => {
    if (!enabled) return;
    
    const currentPath = location.pathname;
    
    // Only track if path actually changed
    if (currentPath !== previousPath.current) {
      // Track the page view
      trackPageView(currentPath);
      
      // Update heartbeat to new path
      if (previousPath.current) {
        updateHeartbeatPath(currentPath);
      } else {
        // First page - start heartbeat
        startHeartbeat(currentPath);
      }
      
      previousPath.current = currentPath;
    }
    
    // Cleanup on unmount
    return () => {
      // Don't stop heartbeat on route changes, only on full unmount
    };
  }, [location.pathname, enabled]);
  
  // Handle visibility changes - pause heartbeat when tab is hidden
  useEffect(() => {
    if (!enabled) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && previousPath.current) {
        // Resume tracking when tab becomes visible
        startHeartbeat(previousPath.current);
      } else {
        // Stop heartbeat when tab is hidden to save resources
        stopHeartbeat();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);
  
  // Stop heartbeat on component unmount (user logged out, etc.)
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, []);
};

export default usePageTracking;

